import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AllSoldProductsResponse, Product, ProductResponse } from 'src/app/models/core/product.model';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api.service';
import { from, Observable, switchMap } from 'rxjs';
import { PaymentType } from 'src/app/types/constants';
import { PromotionalEvent } from 'src/app/models/core/event.model';

@Injectable({
  providedIn: 'root'
})
export class ProductApiService {
  private baseUrl = `${environment.api_server}/api/v2`;
  private baseUrlV1 = `${environment.api_server}/api`;

  constructor(
    private http: HttpClient,
    private _api: ApiService,
  ) { }

  getTopSellerProducts(eventId: number): Observable<{ data: Product[] }> {
    const url = `${this.baseUrl}/products/events/${eventId}/my-top-products?extra_data=true&total_tickets_by_seller=true`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ data: Product[] }>(url, { headers })
      )
    );
  }

  getAllMyTopSellerProducts(
    eventId: number,
    page = 1,
    perPage = 6,
    ecommerce?: number,
    q?: string
  ): Observable<AllSoldProductsResponse> {
    let url = `${this.baseUrl}/products/events/${eventId}/all-my-sold-products?page=${page}&per_page=${perPage}&extra_data=true&total_tickets_by_seller=true`;

    if (typeof ecommerce === 'number') {
      url += `&ecommerce=${ecommerce}`;
    }
    if (q) {
      url += `&q=${encodeURIComponent(q)}`;
    }

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<AllSoldProductsResponse>(url, { headers })
      )
    );
  }

  getRestrictedProducts(eventId: number): Observable<{ data: Product[] }> {
    const url = `${this.baseUrlV1}/events/${eventId}/products-restricted`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ data: Product[] }>(url, { headers })
      )
    );
  }

  getPromotionalProducts(eventId: number): Observable<{ data: Product[] }> {
    const url = `${this.baseUrlV1}/events/${eventId}/products-promotional`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ data: Product[] }>(url, { headers })
      )
    );
  }

  generateCodeForExclusiveProduct(
    eventId: number,
    quota: number
  ): Observable<{
    success: boolean;
    code: string;
    security_phrase: { phrase: string };
  }> {
    const url = `${this.baseUrlV1}/security-phrase/generate`;
    const body = { event_id: eventId, quota };

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<{
          success: boolean;
          code: string;
          security_phrase: { phrase: string };
        }>(url, body, { headers })
      )
    );
  }

  getAllSellerProducts(
    eventId: number,
    page = 1,
    perPage = 50,
    categoryId?: number,
    paymentType?: PaymentType,
    productType?: string,
    q?: string
  ): Observable<ProductResponse> {
    const params: any = {
      event_id: eventId,
      per_page: perPage,
      page
    };

    // Solo enviar category si categoryId !== -1
    if (categoryId != null && categoryId !== -1) {
      params.category = categoryId;
    }

    if (paymentType && paymentType !== 'all') {
      params.payment_type = paymentType;
    }

    // Solo enviar product_type si no es 'all'
    if (productType && productType !== 'all') {
      params.product_type = productType;
    }

    if (q) {
      params.q = q;
    }

    const url = `${this.baseUrlV1}/sellers/products`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<ProductResponse>(url, { headers, params })
      )
    );
  }


  getAvailablePaymentTypes(eventId: number): Observable<{ has_pay_at_door: boolean, has_direct_sale: boolean }> {
    const params = { event_id: eventId };
    const url = `${this.baseUrlV1}/sellers/products/payment-types`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ has_pay_at_door: boolean; has_direct_sale: boolean }>(url, { headers, params })
      )
    );
  }

  getCategoriesBySeller(
    eventId: number,
    onlyLink: boolean = false,
    page: number = 1,
    perPage: number = 10
  ): Observable<{ data: { id: number; name: string }[] }> {
    const url = `${this.baseUrlV1}/categories/${eventId}`;
    const params = {
      per_page: perPage,
      page,
      only_link: onlyLink,
    };

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ data: { id: number; name: string }[] }>(url, { headers, params })
      )
    );
  }

  getEventsWithPromotionalProducts(): Observable<{ data: PromotionalEvent[] }> {
    const url = `${this.baseUrl}/promotions/events-with-promotional-products`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ data: { id: number; name: string }[] }>(url, { headers })
      )
    );
  }

}
